'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowLeft, FileText, ShieldCheck } from 'lucide-react';
import { useLangDataStore } from '@/utils/util_states';
import { getLegalCopy, type LegalKind } from './legal-copy';
import { languageCodeFromPathname, normalizeLanguageCode } from '@/i18n/config';

export default function LegalPage({ kind, initialLanguageCode }: { kind: LegalKind; initialLanguageCode: string }) {
  const pathname = usePathname();
  const { languageCode: storedLanguageCode, langReady } = useLangDataStore();
  const languageCode = languageCodeFromPathname(pathname)
    ?? (langReady ? storedLanguageCode : normalizeLanguageCode(initialLanguageCode));
  const copy = getLegalCopy(languageCode, kind);
  const Icon = kind === 'privacy' ? ShieldCheck : FileText;

  return (
    <div className="mx-auto max-w-4xl px-5 py-8 sm:px-8 sm:py-12">
      <Link
        href="/settings"
        className="mb-8 inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {copy.backToSettings}
      </Link>

      <div>
        <div className="mb-10 border-b border-border pb-8">
          <div className="mb-5 flex items-center gap-3 text-lamp">
            <Icon className="h-6 w-6" aria-hidden="true" />
            <span className="text-[10px] uppercase tracking-[3px] text-muted-foreground">
              {copy.eyebrow}
            </span>
          </div>
          <h1 className="max-w-full break-words font-[family-name:var(--font-playfair)] text-3xl font-semibold tracking-wide sm:text-4xl">
            {copy.title}
          </h1>
        </div>

        <article className="break-words text-sm leading-7 text-muted-foreground sm:text-[15px]">
          {copy.blocks.map((block, index) => {
            if (block.kind === 'heading') {
              return (
                <h2 key={`${block.kind}-${index}`} className="mb-4 mt-10 font-[family-name:var(--font-playfair)] text-xl font-semibold tracking-wide text-foreground first:mt-0 sm:text-2xl">
                  {block.text}
                </h2>
              );
            }
            if (block.kind === 'subheading') {
              return (
                <h3 key={`${block.kind}-${index}`} className="mb-3 mt-7 text-base font-semibold text-foreground sm:text-lg">
                  {block.text}
                </h3>
              );
            }
            if (block.kind === 'list') {
              return (
                <ol key={`${block.kind}-${index}`} className="mb-5 ml-5 list-decimal space-y-2 pl-2">
                  {block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}
                </ol>
              );
            }
            return <p key={`${block.kind}-${index}`} className="mb-5">{block.text}</p>;
          })}
        </article>

        <div className="mt-12 flex justify-center">
          <Link
            href="/settings"
            className="inline-flex items-center gap-2 border border-lamp/30 bg-lamp-dim px-4 py-2.5 text-xs text-lamp transition-colors hover:border-lamp/50 hover:bg-lamp-glow"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {copy.backToSettings}
          </Link>
        </div>
      </div>
    </div>
  );
}
